import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { Clock, Zap, FlaskConical, User, Utensils, Stethoscope, UserCog, Settings, CalendarClock, FileText, ChevronDown, TrendingDown, Check } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { authService } from '../../services/auth/authService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems, SECTION_LABELS, type MealPlanStep, type MealPlanTab } from '../../components/layout/DashboardSidebar/sidebarItems';
import {
  patientsService,
  PatientMealPlan,
} from '../../services/patients/patientsService';
import {
  nutritionProfileService,
  NutritionProfile,
} from '../../services/nutrition/nutritionProfileService';
import MealPlanMonitoringTab from './MealPlanMonitoringTab';
import './PatientDetailPage.css';

/** Nutrient key -> display label, unit, and section for Delta Matrix (backend keys: calories_kcal, protein_g, etc.) */
const NUTRIENT_DELTA_MATRIX_CONFIG: Record<string, { label: string; unit: string; section: 'macronutrients' | 'vitamins_minerals' }> = {
  calories_kcal: { label: 'Calories', unit: 'Kcal', section: 'macronutrients' },
  protein_g: { label: 'Total Protein', unit: 'Grams (g)', section: 'macronutrients' },
  carbohydrates_g: { label: 'Carbohydrates', unit: 'Grams (g)', section: 'macronutrients' },
  fat_g: { label: 'Fat', unit: 'Grams (g)', section: 'macronutrients' },
  fiber_g: { label: 'Fiber', unit: 'Grams (g)', section: 'macronutrients' },
  vitamin_a_mcg: { label: 'Vitamin A', unit: 'mcg', section: 'vitamins_minerals' },
  vitamin_b1_mg: { label: 'Vitamin B1', unit: 'mg', section: 'vitamins_minerals' },
  vitamin_b2_mg: { label: 'Vitamin B2', unit: 'mg', section: 'vitamins_minerals' },
  vitamin_b3_mg: { label: 'Vitamin B3', unit: 'mg', section: 'vitamins_minerals' },
  vitamin_b6_mg: { label: 'Vitamin B6', unit: 'mg', section: 'vitamins_minerals' },
  vitamin_b9_folate_mcg: { label: 'Folate (B9)', unit: 'mcg', section: 'vitamins_minerals' },
  vitamin_b12_mcg: { label: 'Vitamin B12', unit: 'mcg', section: 'vitamins_minerals' },
  vitamin_c_mg: { label: 'Vitamin C', unit: 'mg', section: 'vitamins_minerals' },
  vitamin_d_mcg: { label: 'Vitamin D', unit: 'µg', section: 'vitamins_minerals' },
  vitamin_e_mg: { label: 'Vitamin E', unit: 'mg', section: 'vitamins_minerals' },
  vitamin_k_mcg: { label: 'Vitamin K', unit: 'mcg', section: 'vitamins_minerals' },
  calcium_mg: { label: 'Calcium', unit: 'mg', section: 'vitamins_minerals' },
  magnesium_mg: { label: 'Magnesium', unit: 'mg', section: 'vitamins_minerals' },
  iron_mg: { label: 'Iron', unit: 'mg', section: 'vitamins_minerals' },
  zinc_mg: { label: 'Zinc', unit: 'mg', section: 'vitamins_minerals' },
  phosphorus_mg: { label: 'Phosphorus', unit: 'mg', section: 'vitamins_minerals' },
  potassium_mg: { label: 'Potassium', unit: 'mg', section: 'vitamins_minerals' },
  sodium_mg: { label: 'Sodium', unit: 'mg', section: 'vitamins_minerals' },
  copper_mg: { label: 'Copper', unit: 'mg', section: 'vitamins_minerals' },
  manganese_mg: { label: 'Manganese', unit: 'mg', section: 'vitamins_minerals' },
  selenium_mcg: { label: 'Selenium', unit: 'mcg', section: 'vitamins_minerals' },
};

const FOOD_GROUP_CHART_CONFIG = [
  { key: 'cereals_millets_g', shortLabel: 'Cereals', unit: 'g' },
  { key: 'pulses_beans_g', shortLabel: 'Pulses', unit: 'g' },
  { key: 'glv_g', shortLabel: 'GLV', unit: 'g' },
  { key: 'vegetables_g', shortLabel: 'Veg', unit: 'g' },
  { key: 'roots_tubers_g', shortLabel: 'Roots', unit: 'g' },
  { key: 'fruits_g', shortLabel: 'Fruits', unit: 'g' },
  { key: 'nuts_g', shortLabel: 'Nuts', unit: 'g' },
  { key: 'milk_curd_ml', shortLabel: 'Milk', unit: 'ml' },
  { key: 'fats_oils_g', shortLabel: 'Fats', unit: 'g' },
  { key: 'meat_poultry_g', shortLabel: 'Meat', unit: 'g' },
  { key: 'fish_seafood_g', shortLabel: 'Fish', unit: 'g' },
  { key: 'egg_g', shortLabel: 'Eggs', unit: 'g' },
];

function MacronutrientDonut({
  totalKcal,
  protein,
  carbs,
  fat,
  fiber,
  animated = false,
}: {
  totalKcal: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  animated?: boolean;
}) {
  const kcalFromProtein = protein * 4;
  const kcalFromCarbs = carbs * 4;
  const kcalFromFat = fat * 9;
  const kcalFromFiber = fiber * 2;
  const total = kcalFromProtein + kcalFromCarbs + kcalFromFat + kcalFromFiber;
  const getShare = (kcal: number) => (total > 0 ? (kcal / total) * 100 : 25);
  const shares = [
    { pct: getShare(kcalFromCarbs), color: '#3b82f6' },
    { pct: getShare(kcalFromFat), color: '#8b5cf6' },
    { pct: getShare(kcalFromFiber), color: '#22c55e' },
    { pct: getShare(kcalFromProtein), color: '#1e40af' },
  ];
  let offset = 0;
  const segments = shares.map((s, i) => {
    const start = offset;
    offset += s.pct;
    return { ...s, start, end: offset };
  });
  const r = 48;
  const stroke = 12;
  const circumference = 2 * Math.PI * r;
  return (
    <div className="nutritional-donut-wrap">
      <svg className="nutritional-donut-svg" viewBox="0 0 120 120">
        {segments.map((seg, i) => {
          const dashLength = animated ? (seg.pct / 100) * circumference : 0;
          return (
            <circle
              key={i}
              className="nutritional-donut-segment"
              cx="60"
              cy="60"
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={stroke}
              strokeDasharray={`${dashLength} ${circumference}`}
              strokeDashoffset={-(seg.start / 100) * circumference}
              transform="rotate(-90 60 60)"
            />
          );
        })}
      </svg>
      <div className="nutritional-donut-center">
        <span className="nutritional-donut-kcal">{(totalKcal / 1000).toFixed(1)}k</span>
        <span className="nutritional-donut-label">TOTAL KCAL</span>
      </div>
    </div>
  );
}

/** URL path segment -> internal tab key. Default section is daily-nutrition. */
const SECTION_TO_TAB: Record<string, 'summary' | 'profile' | 'targets' | 'mealPlan' | 'qa' | 'monitoring'> = {
  'daily-nutrition': 'summary',
  'nutrition-profile': 'profile',
  'plan-targets': 'targets',
  'daily-meals': 'mealPlan',
  'qa': 'qa',
  'monitoring': 'monitoring',
};
const TAB_TO_SECTION: Record<'summary' | 'profile' | 'targets' | 'mealPlan' | 'qa' | 'monitoring', string> = {
  summary: 'daily-nutrition',
  profile: 'nutrition-profile',
  targets: 'plan-targets',
  mealPlan: 'daily-meals',
  qa: 'qa',
  monitoring: 'monitoring',
};

/** Page title for each section (breadcrumb + header) */
const SECTION_PAGE_TITLES: Record<'summary' | 'profile' | 'targets' | 'mealPlan' | 'qa' | 'monitoring', string> = {
  summary: 'Daily Nutrition',
  profile: 'Nutrition Profile',
  targets: 'Plan Targets',
  mealPlan: 'Meal Plan',
  qa: "Agent's Q&A",
  monitoring: 'Monitoring',
};

/** Meal plan sub-page under daily-meals: list, new (create), view, edit */
export type MealPlanViewMode = 'list' | 'new' | 'view' | 'edit';

const DietPlanDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId, goalId, section: urlSection } = useParams<{
    patientId: string;
    goalId: string;
    section?: string;
    planId?: string;
  }>();
  const [mealPlan, setMealPlan] = useState<PatientMealPlan | null>(null);
  const [linkedProfile, setLinkedProfile] = useState<NutritionProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Derive active tab and meal-plan sub-view from URL (pathname + params)
  const pathname = location.pathname;
  const isDailyMealsRoute = pathname.includes('/daily-meals');
  // Meal plan list/create/view/edit are now separate pages (MealPlanListPage, etc.).

  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');
  const resolvedSection =
    isDailyMealsRoute ? 'daily-meals' : (urlSection ?? (tabParam && TAB_TO_SECTION[tabParam as keyof typeof TAB_TO_SECTION]) ?? 'daily-nutrition');
  const activeTab = SECTION_TO_TAB[resolvedSection] ?? 'summary';
  const currentSection = TAB_TO_SECTION[activeTab];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patientName, setPatientName] = useState<string | null>(null);
  const [foodGroupLegendVisible, setFoodGroupLegendVisible] = useState({ current: true, ai: true, doctor: true });
  const [nutritionChartsAnimated, setNutritionChartsAnimated] = useState(false);
  const [profileSectionsOpen, setProfileSectionsOpen] = useState<Record<string, boolean>>({
    basics: true,
    foodPreferences: false,
    medicalHistory: false,
    humanContext: false,
    constraints: false,
    mealPattern: false,
    notes: false,
  });
  const toggleProfileSection = (key: string) =>
    setProfileSectionsOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (activeTab !== 'summary') {
      setNutritionChartsAnimated(false);
      return;
    }
    const t = setTimeout(() => setNutritionChartsAnimated(true), 80);
    return () => clearTimeout(t);
  }, [activeTab]);

  // Redirect to path-based section when section is missing, invalid, or legacy ?tab= is used
  // Skip redirect when on daily-meals sub-routes (list/new/view/edit)
  useEffect(() => {
    if (!patientId || !goalId) return;
    if (isDailyMealsRoute) return;
    const base = `/patients/${patientId}/diet-plan/${goalId}`;
    const search = location.search || '';
    if (tabParam && TAB_TO_SECTION[tabParam as keyof typeof TAB_TO_SECTION]) {
      navigate(`${base}/${TAB_TO_SECTION[tabParam as keyof typeof TAB_TO_SECTION]}${search}`, { replace: true });
      return;
    }
    if (!urlSection || !SECTION_TO_TAB[urlSection]) {
      navigate(`${base}/daily-nutrition${search}`, { replace: true });
    }
  }, [patientId, goalId, urlSection, tabParam, navigate, location.search, isDailyMealsRoute]);

  const loadMealPlan = useCallback(async () => {
    if (!patientId || !goalId) {
      setError('Invalid patient or diet plan identifier');
      setLoading(false);
      return;
    }

    const numericPatientId = Number(patientId);
    const numericGoalId = Number(goalId);
    
    if (Number.isNaN(numericPatientId) || Number.isNaN(numericGoalId)) {
      setError('Invalid patient or diet plan identifier');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await patientsService.getPatientMealPlanByGoalId(
        numericPatientId,
        numericGoalId,
      );
      if (!data) {
        setError('Meal plan not found');
        return;
      }
      setMealPlan(data);

      // Load patient name for breadcrumb
      try {
        const patients = await patientsService.getPatients();
        const patient = patients.find((p) => p.id === numericPatientId);
        setPatientName(patient?.name ?? null);
      } catch {
        setPatientName(null);
      }

      // Load the nutrition profile snapshot associated with this meal plan, if any
      const nutritionProfileId = data.mealPlan?.nutritionProfileId;
      if (nutritionProfileId) {
        setProfileLoading(true);
        try {
          const profile = await nutritionProfileService.getProfileById(nutritionProfileId);
          setLinkedProfile(profile);
        } catch (e: any) {
          console.error('Failed to load linked nutrition profile', e);
        } finally {
          setProfileLoading(false);
        }
      } else {
        setLinkedProfile(null);
      }
    } catch (err: any) {
      console.error('Failed to load meal plan:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load diet plan');
    } finally {
      setLoading(false);
    }
  }, [patientId, goalId]);

  useEffect(() => {
    loadMealPlan();
  }, [loadMealPlan]);

  // Calculate daily totals for macros, vitamins, and minerals
  const calculateDailyTotals = () => {
    // Define all vitamins and minerals we want to show (even if 0)
    const allVitaminFields = [
      { key: 'vitamin_a_mcg', label: 'Vit A', unit: 'mcg' },
      { key: 'vitamin_b1_mg', label: 'Vit B1 (Thiamine)', unit: 'mg' },
      { key: 'vitamin_b2_mg', label: 'Vit B2 (Riboflavin)', unit: 'mg' },
      { key: 'vitamin_b3_mg', label: 'Vit B3 (Niacin)', unit: 'mg' },
      { key: 'vitamin_b6_mg', label: 'Vit B6', unit: 'mg' },
      { key: 'vitamin_b12_mcg', label: 'Vit B12', unit: 'mcg' },
      { key: 'vitamin_c_mg', label: 'Vit C', unit: 'mg' },
      { key: 'vitamin_d_mcg', label: 'Vit D', unit: 'mcg' },
      { key: 'vitamin_e_mg', label: 'Vit E', unit: 'mg' },
      { key: 'vitamin_k_mcg', label: 'Vit K', unit: 'mcg' },
      { key: 'folate_mcg', label: 'Folate', unit: 'mcg' },
    ];

    const allMineralFields = [
      { key: 'calcium_mg', label: 'Calcium', unit: 'mg' },
      { key: 'iron_mg', label: 'Iron', unit: 'mg' },
      { key: 'magnesium_mg', label: 'Magnesium', unit: 'mg' },
      { key: 'phosphorus_mg', label: 'Phosphorus', unit: 'mg' },
      { key: 'potassium_mg', label: 'Potassium', unit: 'mg' },
      { key: 'sodium_mg', label: 'Sodium', unit: 'mg' },
      { key: 'zinc_mg', label: 'Zinc', unit: 'mg' },
      { key: 'copper_mg', label: 'Copper', unit: 'mg' },
      { key: 'manganese_mg', label: 'Manganese', unit: 'mg' },
      { key: 'selenium_mcg', label: 'Selenium', unit: 'mcg' },
    ];

    const emptyTotals = {
      totalCalories: 0,
      macros: { protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 },
      vitamins: allVitaminFields.map(v => ({ ...v, value: 0 })),
      minerals: allMineralFields.map(m => ({ ...m, value: 0 })),
    };

    // If meal recipes/options are not present (we no longer generate/store them in the workflow),
    // derive daily totals from saved goal targets instead.
    const targets: any[] = Array.isArray((mealPlan as any)?.targets) ? (mealPlan as any).targets : [];
    if ((!mealPlan || !mealPlan.mealPlan) && targets.length === 0) {
      return emptyTotals;
    }

    const mealKeys: ('breakfast' | 'lunch' | 'dinner' | 'snacks')[] = ['breakfast', 'lunch', 'dinner', 'snacks'];
    const hasAnyRecipes =
      !!mealPlan?.mealPlan &&
      mealKeys.some((mealKey) => {
        const meal: any = (mealPlan.mealPlan as any)[mealKey];
        const options: any[] = Array.isArray(meal?.options) ? meal.options : [];
        return options.length > 0;
      });

    if (((!mealPlan || !mealPlan.mealPlan) || !hasAnyRecipes) && targets.length > 0) {
      const totals = {
        totalCalories: 0,
        macros: { protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 },
        vitamins: allVitaminFields.map(v => ({ ...v, value: 0 })),
        minerals: allMineralFields.map(m => ({ ...m, value: 0 })),
      };

      const getTargetValue = (key: string): number => {
        const t = targets.find((x: any) => x?.nutrient?.key === key);
        const v = t?.target_min ?? t?.target_max ?? null;
        return typeof v === 'number' ? v : 0;
      };

      totals.totalCalories = getTargetValue('calories');
      totals.macros.protein_g = getTargetValue('protein_g');
      totals.macros.carbs_g = getTargetValue('carbs_g');
      totals.macros.fat_g = getTargetValue('fat_g');
      totals.macros.fiber_g = getTargetValue('fiber_g');

      totals.vitamins.forEach(v => {
        v.value += getTargetValue(v.key);
      });
      totals.minerals.forEach(m => {
        m.value += getTargetValue(m.key);
      });

      return totals;
    }

    const totals = {
      totalCalories: 0,
      macros: { protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 },
      vitamins: allVitaminFields.map(v => ({ ...v, value: 0 })),
      minerals: allMineralFields.map(m => ({ ...m, value: 0 })),
    };

    const mp: any = mealPlan?.mealPlan ?? null;
    if (!mp) {
      return emptyTotals;
    }

    mealKeys.forEach((mealKey) => {
      const meal: any = mp[mealKey];
      if (!meal) return;

      const options: any[] = Array.isArray(meal.options) ? meal.options : [];
      const recommendedIndex: number | undefined =
        typeof meal.recommended_option === 'number' ? meal.recommended_option : undefined;
      const recommended = recommendedIndex !== undefined ? options[recommendedIndex] : options[0];
      
      if (recommended) {
        // Sum calories
        if (typeof recommended.calories === 'number') {
          totals.totalCalories += recommended.calories;
        }

        // Sum macros
        if (recommended.macros) {
          totals.macros.protein_g += recommended.macros.protein_g || 0;
          totals.macros.carbs_g += recommended.macros.carbs_g || 0;
          totals.macros.fat_g += recommended.macros.fat_g || 0;
          totals.macros.fiber_g += recommended.macros.fiber_g || 0;
        }

        // Sum vitamins and minerals from micronutrients
        if (recommended.micronutrients) {
          const micronutrients = recommended.micronutrients;
          
          // Sum all vitamins
          totals.vitamins.forEach(vitamin => {
            if (typeof micronutrients[vitamin.key] === 'number') {
              vitamin.value += micronutrients[vitamin.key];
            }
          });

          // Sum all minerals
          totals.minerals.forEach(mineral => {
            if (typeof micronutrients[mineral.key] === 'number') {
              mineral.value += micronutrients[mineral.key];
            }
          });
        }
      }
    });

    return totals;
  };

  const dailyTotals = calculateDailyTotals();

  const targetsContext = mealPlan?.targetsContext;

  // Default RDA values for vitamins/minerals (used when plan targets are missing)
  const defaultRDA: Record<string, number> = useMemo(() => ({
    vitamin_a_mcg: 900,
    vitamin_b1_mg: 1.2,
    vitamin_b2_mg: 1.3,
    vitamin_b3_mg: 16,
    vitamin_b6_mg: 1.7,
    vitamin_b12_mcg: 2.4,
    vitamin_c_mg: 90,
    vitamin_d_mcg: 15,
    vitamin_e_mg: 15,
    vitamin_k_mcg: 120,
    folate_mcg: 400,
    calcium_mg: 2000,
    iron_mg: 18,
    magnesium_mg: 420,
    phosphorus_mg: 700,
    potassium_mg: 3400,
    sodium_mg: 2300,
    zinc_mg: 11,
    copper_mg: 0.9,
    manganese_mg: 2.3,
    selenium_mcg: 55,
  }), []);

  const getRecommended = useCallback((key: string): number => {
    const targets: any[] = Array.isArray((mealPlan as any)?.targets) ? (mealPlan as any).targets : [];
    const t = targets.find((x: any) => x?.nutrient?.key === key);
    const v = t?.target_max ?? t?.target_min ?? null;
    if (typeof v === 'number' && v > 0) return v;
    return defaultRDA[key] ?? 0;
  }, [mealPlan, defaultRDA]);

  // Keys used in baseIcmrNutrients / doctorModifiedNutrients may differ (e.g. vitamin_b9_folate_mcg vs folate_mcg)
  const nutrientKeyForPlan = useCallback((key: string): string[] => {
    if (key === 'folate_mcg') return ['folate_mcg', 'vitamin_b9_folate_mcg'];
    return [key];
  }, []);

  const getIcmrAndDoctorAdjusted = useCallback((key: string): { icmrBase: number | null; doctorAdjusted: number | null } => {
    const mp = mealPlan?.mealPlan as any;
    if (!mp) return { icmrBase: null, doctorAdjusted: null };
    const keys = nutrientKeyForPlan(key);
    let icmrBase: number | null = null;
    let doctorAdjusted: number | null = null;
    for (const k of keys) {
      if (icmrBase === null && typeof mp.baseIcmrNutrients?.[k] === 'number') icmrBase = mp.baseIcmrNutrients[k];
      if (doctorAdjusted === null && typeof mp.doctorModifiedNutrients?.[k] === 'number') doctorAdjusted = mp.doctorModifiedNutrients[k];
      if (doctorAdjusted === null && typeof mp.adjustedNutrients?.[k] === 'number') doctorAdjusted = mp.adjustedNutrients[k];
    }
    return { icmrBase, doctorAdjusted };
  }, [mealPlan, nutrientKeyForPlan]);

  /** Status: ABOVE TARGET (>200%), OPTIMAL (95–200% or ≥100%), AT RISK (50–95%), DEFICIENT (<50%). Near-target (95–100%) treated as OPTIMAL. */
  const getNutrientStatus = (current: number, recommended: number): 'above_target' | 'optimal' | 'at_risk' | 'deficient' => {
    if (recommended <= 0) return 'optimal';
    const pct = (current / recommended) * 100;
    if (pct > 200) return 'above_target';
    if (pct >= 95) return 'optimal'; /* include 95–100% so "almost matching" is not AT RISK */
    if (pct >= 50) return 'at_risk';
    return 'deficient';
  };

  const vitaminBarColors = ['#2563eb', '#7c3aed', '#db2777', '#dc2626', '#ea580c', '#ca8a04', '#65a30d', '#059669', '#0d9488', '#0891b2', '#4f46e5'];
  const mineralBarColors = ['#1e40af', '#be185d', '#b45309', '#047857', '#0e7490', '#6366f1', '#a21caf', '#15803d', '#c2410c', '#1d4ed8'];

  const foodGroupChartData = useMemo(() => {
    const mp = mealPlan?.mealPlan as any;
    if (!mp) return [];
    return FOOD_GROUP_CHART_CONFIG.map((fg) => {
      const base = (mp.baseIcmrRecommendations?.[fg.key] ?? 0) as number;
      const ai = (mp.foodGroupRecommendations?.[fg.key] ?? 0) as number;
      const doctor = (mp.doctorModifiedRecommendations?.[fg.key] ?? 0) as number;
      return {
        name: fg.shortLabel,
        fullName: fg.key,
        current: typeof base === 'number' ? base : 0,
        ai: typeof ai === 'number' ? ai : 0,
        doctor: typeof doctor === 'number' ? doctor : 0,
      };
    });
  }, [mealPlan]);

  const formatVitaminLabel = (label: string): string => {
    const u = label.toUpperCase();
    if (u.startsWith('VIT A')) return 'VITAMIN A';
    if (u.startsWith('VIT B')) return u.replace('VIT B', 'VITAMIN B');
    if (u.startsWith('VIT C')) return 'VITAMIN C';
    if (u.startsWith('VIT D')) return 'VITAMIN D';
    if (u.startsWith('VIT E')) return 'VITAMIN E';
    if (u.startsWith('VIT K')) return 'VITAMIN K';
    if (u.startsWith('FOLATE')) return 'FOLATE';
    return u;
  };

  const renderValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return value;
  };

  const formatKeyLabel = (key: string): string => {
    if (!key) return '-';
    return key
      .split('_')
      .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ''))
      .join(' ');
  };

  const renderMealSection = (
    label: string,
    mealKey: 'breakfast' | 'lunch' | 'dinner' | 'snacks',
  ) => {
    if (!mealPlan || !mealPlan.mealPlan) return null;
    const meal: any = (mealPlan.mealPlan as any)[mealKey];
    if (!meal) return null;

    const options: any[] = Array.isArray(meal.options) ? meal.options : [];
    const recommendedIndex: number | undefined =
      typeof meal.recommended_option === 'number' ? meal.recommended_option : undefined;
    const recommended = recommendedIndex !== undefined ? options[recommendedIndex] : options[0];
    const totalCalories = meal.total_calories_kcal ?? meal.totalCaloriesKcal;

    return (
      <div className="meal-plan-meal-card">
        <div className="meal-plan-meal-header">
          <h3>{label}</h3>
          {typeof totalCalories === 'number' && (
            <span className="meal-plan-calories">{Math.round(totalCalories)} kcal</span>
          )}
        </div>
        {recommended ? (
          <div className="meal-plan-meal-body">
            <div className="meal-plan-meal-name">{recommended.name || 'Recommended option'}</div>
            {recommended.macros && (
              <div className="meal-plan-macros">
                <span>Protein: {recommended.macros.protein_g} g</span>
                <span>Carbs: {recommended.macros.carbs_g} g</span>
                <span>Fat: {recommended.macros.fat_g} g</span>
                {typeof recommended.macros.fiber_g === 'number' && (
                  <span>Fiber: {recommended.macros.fiber_g} g</span>
                )}
              </div>
            )}
            {recommended.ingredients && Array.isArray(recommended.ingredients) && recommended.ingredients.length > 0 && (
              <div className="meal-plan-ingredients">
                <span className="label">Key ingredients:</span>
                <span className="value">{recommended.ingredients.join(', ')}</span>
              </div>
            )}
            {recommended.notes && (
              <div className="meal-plan-notes">
                <span className="label">Notes:</span>
                <span className="value">{recommended.notes}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="meal-plan-no-option">No options defined for this meal yet.</div>
        )}
      </div>
    );
  };

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  const mealPlanStepParam = searchParams.get('step');
  const mealPlanTabParam = searchParams.get('tab');
  const mealPlanStep: MealPlanStep | undefined = resolvedSection === 'daily-meals' && (mealPlanStepParam === 'settings' || mealPlanStepParam === 'view' || mealPlanStepParam === 'draft')
    ? (mealPlanStepParam === 'settings' ? 'settings' : mealPlanStepParam === 'view' ? 'view' : 'draft')
    : undefined;
  const mealPlanTab: MealPlanTab | undefined = resolvedSection === 'daily-meals' && mealPlanStep === 'draft'
    ? (mealPlanTabParam === 'daily_meal_plan' ? 'daily_meal_plan' : mealPlanTabParam === 'chat' ? 'chat' : 'nutrient_comparison')
    : undefined;

  const sidebarContext = patientId && goalId
    ? {
        patientId,
        patientName: patientName ?? undefined,
        goalId,
        mealPlanName: mealPlan?.goalName ?? undefined,
        mealPlanSection: currentSection,
        mealPlanStep,
        mealPlanTab,
        requirementsChatSessionId: mealPlan?.requirementsChatSessionId ?? undefined,
      }
    : undefined;
  const sidebarItems = createSidebarItems(navigate, location.pathname, sidebarContext);

  if (loading) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="loading-message">Loading diet plan details...</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error || !mealPlan) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="error-message">Error: {error || 'Meal plan not found'}</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />
        <div className="dashboard-container">
          <main className="dashboard-main-content">
            <div className="section-card">
              <div className="meal-plan-content">
                <nav className="detail-breadcrumb" aria-label="Breadcrumb">
                  <Link to="/patients" className="breadcrumb-link">Patients</Link>
                  <span className="breadcrumb-sep" aria-hidden>›</span>
                  {patientId && (
                    <>
                      <Link to={`/patients/${patientId}`} className="breadcrumb-link">{patientName ?? 'Patient'}</Link>
                      <span className="breadcrumb-sep" aria-hidden>›</span>
                      <Link to={`/patients/${patientId}/diet-plans`} className="breadcrumb-link">Diet Plans</Link>
                      <span className="breadcrumb-sep" aria-hidden>›</span>
                      {goalId && (
                        <>
                          <Link to={`/patients/${patientId}/diet-plan/${goalId}/daily-nutrition`} className="breadcrumb-link">{mealPlan?.goalName ?? 'Diet Plan'}</Link>
                          <span className="breadcrumb-sep" aria-hidden>›</span>
                        </>
                      )}
                    </>
                  )}
                  {resolvedSection === 'daily-meals' && mealPlanStep ? (
                    mealPlanStep === 'view' ? (
                      <span className="breadcrumb-current">{SECTION_LABELS['daily-meals']}</span>
                    ) : (
                    <>
                      <Link to={patientId && goalId ? `/patients/${patientId}/diet-plan/${goalId}/daily-meals` : '#'} className="breadcrumb-link">{SECTION_LABELS['daily-meals']}</Link>
                      <span className="breadcrumb-sep" aria-hidden>›</span>
                      <span className="breadcrumb-current">
                        {mealPlanStep === 'settings' ? 'Settings' : 'Daily Meal Plan'}
                      </span>
                    </>
                    )
                  ) : (
                    <span className="breadcrumb-current">
                      {SECTION_LABELS[resolvedSection] ?? SECTION_PAGE_TITLES[activeTab]}
                    </span>
                  )}
                </nav>
                <h1 className="meal-plan-page-title">{SECTION_LABELS[resolvedSection] ?? SECTION_PAGE_TITLES[activeTab]}</h1>
                <div className="meal-plan-detail-meta-card">
                  <span className="meal-plan-detail-meta-label">Diet Plan</span>
                  <div className="meal-plan-detail-meta-line">
                    <span className="meal-plan-detail-meta-name">{mealPlan.goalName}</span>
                    <span className="meal-plan-detail-meta-date">
                      Effective: {mealPlan.effectiveAt ? new Date(mealPlan.effectiveAt).toLocaleDateString() : '—'}
                    </span>
                  </div>
                </div>
                {activeTab === 'summary' && (
                  <>
                  {/* NUTRITIONAL HEALTH - Three cards */}
                  <h2 className="nutritional-health-title">NUTRITIONAL HEALTH</h2>
                  <div className="nutritional-health-cards">
                    {/* Card 1: MACRONUTRIENTS */}
                    <div className="nutritional-health-card">
                      <div className="nutritional-health-card-header">
                        <span>MACRONUTRIENTS</span>
                        <Clock size={16} className="nutritional-health-card-icon" />
                      </div>
                      <MacronutrientDonut
                        animated={nutritionChartsAnimated}
                        totalKcal={Math.round(dailyTotals.totalCalories || (mealPlan.mealPlan as any)?.totalCaloriesKcal || 0)}
                        protein={dailyTotals.macros.protein_g}
                        carbs={dailyTotals.macros.carbs_g}
                        fat={dailyTotals.macros.fat_g}
                        fiber={dailyTotals.macros.fiber_g}
                      />
                      <div className="nutritional-macro-legend">
                        <div className="nutritional-macro-item">
                          <div className="nutritional-macro-item-row">
                            <span className="nutritional-macro-dot nutritional-macro-dot-protein" />
                            <span className="nutritional-macro-label">PROTEIN</span>
                          </div>
                          <span className="nutritional-macro-value">{dailyTotals.macros.protein_g.toFixed(0)}g</span>
                        </div>
                        <div className="nutritional-macro-item">
                          <div className="nutritional-macro-item-row">
                            <span className="nutritional-macro-dot nutritional-macro-dot-carbs" />
                            <span className="nutritional-macro-label">CARBS</span>
                          </div>
                          <span className="nutritional-macro-value">{dailyTotals.macros.carbs_g.toFixed(0)}g</span>
                        </div>
                        <div className="nutritional-macro-item">
                          <div className="nutritional-macro-item-row">
                            <span className="nutritional-macro-dot nutritional-macro-dot-fat" />
                            <span className="nutritional-macro-label">FATS</span>
                          </div>
                          <span className="nutritional-macro-value">{dailyTotals.macros.fat_g.toFixed(0)}g</span>
                        </div>
                        <div className="nutritional-macro-item">
                          <div className="nutritional-macro-item-row">
                            <span className="nutritional-macro-dot nutritional-macro-dot-fiber" />
                            <span className="nutritional-macro-label">FIBER</span>
                          </div>
                          <span className="nutritional-macro-value">{dailyTotals.macros.fiber_g.toFixed(0)}g</span>
                        </div>
                      </div>
                    </div>

                    {/* Card 2: KEY VITAMINS */}
                    <div className="nutritional-health-card">
                      <div className="nutritional-health-card-header">
                        <span>KEY VITAMINS</span>
                        <Zap size={16} className="nutritional-health-card-icon" />
                      </div>
                      <div className="nutritional-scroll-list nutritional-vitamins-list">
                        {dailyTotals.vitamins.map((vitamin, idx) => {
                          const { icmrBase, doctorAdjusted } = getIcmrAndDoctorAdjusted(vitamin.key);
                          const hasPlanValues = icmrBase !== null || doctorAdjusted !== null;
                          const targetForDisplay = doctorAdjusted ?? icmrBase;
                          const adjValue = doctorAdjusted ?? (mealPlan?.mealPlan as any)?.adjustedNutrients?.[vitamin.key];
                          const baseValue = icmrBase;
                          const showDoctorVsIcmr = icmrBase !== null && (doctorAdjusted !== null || (mealPlan?.mealPlan as any)?.adjustedNutrients?.[vitamin.key] != null);
                          const rec = showDoctorVsIcmr && (typeof adjValue === 'number' || baseValue !== null)
                            ? (typeof adjValue === 'number' ? adjValue : baseValue!)
                            : (hasPlanValues ? (targetForDisplay ?? getRecommended(vitamin.key)) : getRecommended(vitamin.key));
                          const targetForBar = showDoctorVsIcmr && baseValue != null ? baseValue : rec;
                          const status = getNutrientStatus(vitamin.value, targetForBar);
                          const pctRaw = targetForBar > 0 ? (vitamin.value / targetForBar) * 100 : 0;
                          const pct = Math.min(Math.max(0, pctRaw), 100);
                          const barColor = vitaminBarColors[idx % vitaminBarColors.length];
                          const aboveTargetColor = '#b45309';
                          return (
                            <div key={vitamin.key} className="nutritional-vitamin-row">
                              <div className="nutritional-label-value-row">
                                <span className="nutritional-vitamin-label">{formatVitaminLabel(vitamin.label)}</span>
                                <span className="nutritional-vitamin-value">
                                  {showDoctorVsIcmr ? (
                                    <>
                                      <strong>{(typeof adjValue === 'number' ? adjValue : baseValue)?.toFixed(1) ?? '-'}{vitamin.unit}</strong>
                                      <span className="nutritional-value-sep"> / </span>
                                      <span>{baseValue != null ? `${baseValue.toFixed(1)}${vitamin.unit}` : '-'}</span>
                                    </>
                                  ) : (
                                    <>
                                      <strong>{vitamin.value.toFixed(1)}{vitamin.unit}</strong> / {rec.toFixed(1)}{vitamin.unit}
                                    </>
                                  )}
                                </span>
                              </div>
                              <div className="nutritional-progress-wrap">
                                <div className="nutritional-progress-track">
                                  <div
                                    className="nutritional-progress-fill"
                                    style={{
                                      width: `${nutritionChartsAnimated ? pct : 0}%`,
                                      backgroundColor: status === 'above_target' ? aboveTargetColor : barColor,
                                    }}
                                  />
                                </div>
                                <span
                                  className={`nutritional-status ${status === 'above_target' ? 'nutritional-status-above-target' : ''}`}
                                  style={{ color: status === 'above_target' ? aboveTargetColor : barColor }}
                                >
                                  {status === 'above_target' ? 'ABOVE TARGET' : status === 'optimal' ? 'OPTIMAL' : status === 'at_risk' ? 'AT RISK' : 'DEFICIENT'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {(mealPlan.mealPlan as any)?.baseIcmrNutrients && (
                        <p className="nutritional-card-legend">
                          Vitamins and minerals show <strong>Doctor adjusted</strong> / <strong>ICMR Base</strong>.
                        </p>
                      )}
                    </div>

                    {/* Card 3: DAILY MINERALS */}
                    <div className="nutritional-health-card nutritional-health-card-minerals">
                      <div className="nutritional-health-card-header">
                        <span>DAILY MINERALS</span>
                        <FlaskConical size={16} className="nutritional-health-card-icon" />
                      </div>
                      <div className="nutritional-scroll-list nutritional-minerals-list">
                        {dailyTotals.minerals.map((mineral, idx) => {
                          const { icmrBase, doctorAdjusted } = getIcmrAndDoctorAdjusted(mineral.key);
                          const hasPlanValues = icmrBase !== null || doctorAdjusted !== null;
                          const targetForDisplay = doctorAdjusted ?? icmrBase;
                          const adjValue = doctorAdjusted ?? (mealPlan?.mealPlan as any)?.adjustedNutrients?.[mineral.key];
                          const baseValue = icmrBase;
                          const showDoctorVsIcmr = icmrBase !== null && (doctorAdjusted !== null || (mealPlan?.mealPlan as any)?.adjustedNutrients?.[mineral.key] != null);
                          const rec = showDoctorVsIcmr && (typeof adjValue === 'number' || baseValue !== null)
                            ? (typeof adjValue === 'number' ? adjValue : baseValue!)
                            : (hasPlanValues ? (targetForDisplay ?? getRecommended(mineral.key)) : getRecommended(mineral.key));
                          const targetForBar = showDoctorVsIcmr && baseValue != null ? baseValue : rec;
                          const status = getNutrientStatus(mineral.value, targetForBar);
                          const pctRaw = targetForBar > 0 ? (mineral.value / targetForBar) * 100 : 0;
                          const pct = Math.min(Math.max(0, pctRaw), 100);
                          const barColor = mineralBarColors[idx % mineralBarColors.length];
                          const aboveTargetColor = '#b45309';
                          return (
                            <div key={mineral.key} className="nutritional-mineral-row">
                              <div className="nutritional-label-value-row">
                                <span className="nutritional-mineral-label">{mineral.label.toUpperCase()}</span>
                                <span className="nutritional-mineral-value">
                                  {showDoctorVsIcmr ? (
                                    <>
                                      <strong>{(typeof adjValue === 'number' ? adjValue : baseValue)?.toFixed(1) ?? '-'}{mineral.unit}</strong>
                                      <span className="nutritional-value-sep"> / </span>
                                      <span>{baseValue != null ? `${baseValue.toFixed(1)}${mineral.unit}` : '-'}</span>
                                    </>
                                  ) : (
                                    <>
                                      {mineral.value.toFixed(1)}{mineral.unit} / {rec.toFixed(1)}{mineral.unit}
                                    </>
                                  )}
                                </span>
                              </div>
                              <div className="nutritional-progress-wrap">
                                <div className="nutritional-progress-track">
                                  <div
                                    className="nutritional-progress-fill"
                                    style={{
                                      width: `${nutritionChartsAnimated ? pct : 0}%`,
                                      backgroundColor: status === 'above_target' ? aboveTargetColor : barColor,
                                    }}
                                  />
                                </div>
                                <span
                                  className={`nutritional-status ${status === 'above_target' ? 'nutritional-status-above-target' : ''}`}
                                  style={{ color: status === 'above_target' ? aboveTargetColor : barColor }}
                                >
                                  {status === 'above_target' ? 'ABOVE TARGET' : status === 'optimal' ? 'OPTIMAL' : status === 'at_risk' ? 'AT RISK' : 'DEFICIENT'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {(mealPlan.mealPlan as any)?.baseIcmrNutrients && (
                        <p className="nutritional-card-legend">
                          Vitamins and minerals show <strong>Doctor adjusted</strong> / <strong>ICMR Base</strong>.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Food Group Recommendations Comparison - Grouped Bar Chart */}
                  {(mealPlan.mealPlan?.baseIcmrRecommendations || mealPlan.mealPlan?.foodGroupRecommendations || mealPlan.mealPlan?.doctorModifiedRecommendations) && (
                    <div className="food-group-chart-card">
                      <h4 className="food-group-chart-title">Food group comparison</h4>
                      <div className="food-group-chart-legend-row">
                        <span
                          className={`food-group-legend-item ${foodGroupLegendVisible.current ? '' : 'food-group-legend-inactive'}`}
                          onClick={() => setFoodGroupLegendVisible((v) => ({ ...v, current: !v.current }))}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && setFoodGroupLegendVisible((v) => ({ ...v, current: !v.current }))}
                        >
                          <span className="food-group-legend-dot" style={{ backgroundColor: '#9ca3af' }} />
                          CURRENT MEAL PLAN
                        </span>
                        <span
                          className={`food-group-legend-item ${foodGroupLegendVisible.ai ? '' : 'food-group-legend-inactive'}`}
                          onClick={() => setFoodGroupLegendVisible((v) => ({ ...v, ai: !v.ai }))}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && setFoodGroupLegendVisible((v) => ({ ...v, ai: !v.ai }))}
                        >
                          <span className="food-group-legend-dot" style={{ backgroundColor: '#a78bfa' }} />
                          AI RECOMMENDATION
                        </span>
                        <span
                          className={`food-group-legend-item ${foodGroupLegendVisible.doctor ? '' : 'food-group-legend-inactive'}`}
                          onClick={() => setFoodGroupLegendVisible((v) => ({ ...v, doctor: !v.doctor }))}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && setFoodGroupLegendVisible((v) => ({ ...v, doctor: !v.doctor }))}
                        >
                          <span className="food-group-legend-dot" style={{ backgroundColor: '#4f46e5' }} />
                          DOCTOR PRESCRIBED
                        </span>
                      </div>
                      <div className="food-group-chart-wrap">
                        <ResponsiveContainer width="100%" height={320}>
                          <BarChart data={foodGroupChartData} margin={{ top: 12, right: 12, left: 0, bottom: 8 }} barCategoryGap="12%" barGap={6}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} width={32} domain={[0, (dataMax: number) => Math.max(350, Math.ceil((dataMax || 0) / 50) * 50)]} />
                            <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} contentStyle={{ fontSize: '12px', borderRadius: '8px' }} />
                            {foodGroupLegendVisible.current && <Bar dataKey="current" name="CURRENT MEAL PLAN" fill="#9ca3af" barSize={20} radius={[6, 6, 6, 6]} />}
                            {foodGroupLegendVisible.ai && <Bar dataKey="ai" name="AI RECOMMENDATION" fill="#a78bfa" barSize={20} radius={[6, 6, 6, 6]} />}
                            {foodGroupLegendVisible.doctor && <Bar dataKey="doctor" name="DOCTOR PRESCRIBED" fill="#4f46e5" barSize={20} radius={[6, 6, 6, 6]} />}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      {(mealPlan.mealPlan?.baseIcmrRecommendations?.source_demographics || mealPlan.mealPlan?.foodGroupRecommendations?.source_demographics || mealPlan.mealPlan?.doctorModifiedRecommendations?.source_demographics) && (() => {
                        const demo = mealPlan.mealPlan?.baseIcmrRecommendations?.source_demographics ?? mealPlan.mealPlan?.foodGroupRecommendations?.source_demographics ?? mealPlan.mealPlan?.doctorModifiedRecommendations?.source_demographics;
                        const age = demo?.age ?? 'N/A';
                        const gender = typeof demo?.gender === 'string' ? demo.gender.toLowerCase() : (demo?.gender ?? 'N/A');
                        const weight = demo?.weight_kg ?? 'N/A';
                        const activity = typeof demo?.activity_level === 'string' ? demo.activity_level.toLowerCase() : (demo?.activity_level ?? 'N/A');
                        return (
                          <div className="food-group-analysis-context">
                            <span className="food-group-analysis-icon" aria-hidden>✓</span>
                            <span className="food-group-analysis-label">Analysis context</span>
                            <span className="food-group-analysis-value">
                              Optimized for: Age <strong>{age}</strong>Y • Gender <strong>{gender}</strong> • Weight <strong>{weight}</strong> kg • Activity <strong>{activity}</strong>
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Nutrients Comparison Table */}
                      {(mealPlan.mealPlan?.baseIcmrNutrients || mealPlan.mealPlan?.adjustedNutrients || mealPlan.mealPlan?.doctorModifiedNutrients) && (
                        <div className="nutrients-comparison-card">
                          <h4 className="nutrients-comparison-card-title">Nutrients Comparison</h4>
                          <div className="nutrients-comparison-table-wrap">
                            <table className="nutrients-comparison-table">
                              <thead>
                                <tr>
                                  <th className="nutrients-th nutrient">Nutrient</th>
                                  <th className="nutrients-th center">ICMR Base</th>
                                  <th className="nutrients-th center">Agent Adjusted</th>
                                  {mealPlan.mealPlan?.doctorModifiedNutrients && (
                                    <th className="nutrients-th center">Doctor Modified</th>
                                  )}
                                  <th className="nutrients-th final">Final Change</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[
                                  // Macros
                                  { key: 'calories_kcal', label: 'Calories', unit: 'kcal' },
                                  { key: 'protein_g', label: 'Protein', unit: 'g' },
                                  { key: 'carbohydrates_g', label: 'Carbohydrates', unit: 'g' },
                                  { key: 'fat_g', label: 'Fat', unit: 'g' },
                                  { key: 'fiber_g', label: 'Fiber', unit: 'g' },
                                  // Vitamins
                                  { key: 'vitamin_a_mcg', label: 'Vitamin A', unit: 'mcg' },
                                  { key: 'vitamin_b1_mg', label: 'Vitamin B1 (Thiamine)', unit: 'mg' },
                                  { key: 'vitamin_b2_mg', label: 'Vitamin B2 (Riboflavin)', unit: 'mg' },
                                  { key: 'vitamin_b3_mg', label: 'Vitamin B3 (Niacin)', unit: 'mg' },
                                  { key: 'vitamin_b6_mg', label: 'Vitamin B6', unit: 'mg' },
                                  { key: 'vitamin_b9_folate_mcg', label: 'Folate (B9)', unit: 'mcg' },
                                  { key: 'vitamin_b12_mcg', label: 'Vitamin B12', unit: 'mcg' },
                                  { key: 'vitamin_c_mg', label: 'Vitamin C', unit: 'mg' },
                                  { key: 'vitamin_d_mcg', label: 'Vitamin D', unit: 'mcg' },
                                  { key: 'vitamin_e_mg', label: 'Vitamin E', unit: 'mg' },
                                  { key: 'vitamin_k_mcg', label: 'Vitamin K', unit: 'mcg' },
                                  // Minerals
                                  { key: 'calcium_mg', label: 'Calcium', unit: 'mg' },
                                  { key: 'magnesium_mg', label: 'Magnesium', unit: 'mg' },
                                  { key: 'iron_mg', label: 'Iron', unit: 'mg' },
                                  { key: 'zinc_mg', label: 'Zinc', unit: 'mg' },
                                  { key: 'phosphorus_mg', label: 'Phosphorus', unit: 'mg' },
                                  { key: 'potassium_mg', label: 'Potassium', unit: 'mg' },
                                  { key: 'sodium_mg', label: 'Sodium', unit: 'mg' },
                                  { key: 'copper_mg', label: 'Copper', unit: 'mg' },
                                  { key: 'manganese_mg', label: 'Manganese', unit: 'mg' },
                                  { key: 'selenium_mcg', label: 'Selenium', unit: 'mcg' },
                                ].map((nutrient, idx) => {
                                  const baseValue = mealPlan.mealPlan?.baseIcmrNutrients?.[nutrient.key] as number | undefined | null;
                                  const agentValue = mealPlan.mealPlan?.adjustedNutrients?.[nutrient.key] as number | undefined | null;
                                  const doctorValue = mealPlan.mealPlan?.doctorModifiedNutrients?.[nutrient.key] as number | undefined | null;
                                  // Final value is doctor-modified if exists, otherwise agent-adjusted
                                  const finalValue = doctorValue !== undefined && doctorValue !== null ? doctorValue : agentValue;
                                  const change = baseValue !== undefined && baseValue !== null && finalValue !== undefined && finalValue !== null ? finalValue - baseValue : null;
                                  const changePercent = baseValue !== undefined && baseValue !== null && baseValue > 0 && finalValue !== undefined && finalValue !== null ? ((finalValue - baseValue) / baseValue * 100) : null;
                                  const isIncrease = change !== null && change > 0;
                                  const isDecrease = change !== null && change < 0;
                                  
                                  // Only show nutrients that have at least one value
                                  if ((baseValue === undefined || baseValue === null) && (agentValue === undefined || agentValue === null) && (doctorValue === undefined || doctorValue === null)) {
                                    return null;
                                  }
                                  
                                  return (
                                    <tr key={nutrient.key} className="nutrients-tr">
                                      <td className="nutrients-td nutrient-label">{nutrient.label}</td>
                                      <td className="nutrients-td center base-value">
                                        {baseValue !== undefined && baseValue !== null ? `${parseFloat(baseValue.toFixed(2))} ${nutrient.unit}` : '-'}
                                      </td>
                                      <td className="nutrients-td center">
                                        {agentValue !== undefined && agentValue !== null ? `${parseFloat(agentValue.toFixed(2))} ${nutrient.unit}` : '-'}
                                      </td>
                                      {mealPlan.mealPlan?.doctorModifiedNutrients && (
                                        <td className="nutrients-td center doctor-value">
                                          {doctorValue !== undefined && doctorValue !== null ? `${parseFloat(doctorValue.toFixed(2))} ${nutrient.unit}` : '-'}
                                        </td>
                                      )}
                                      <td className="nutrients-td final-change">
                                        {change !== null && changePercent !== null ? (
                                          <span className={`nutrients-change ${isIncrease ? 'nutrients-change-up' : isDecrease ? 'nutrients-change-down' : ''}`}>
                                            {isIncrease ? '+' : ''}{parseFloat(change.toFixed(2))} {nutrient.unit} ({isIncrease ? '+' : ''}{changePercent.toFixed(1)}%)
                                          </span>
                                        ) : (
                                          <span className="nutrients-change-empty">-</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                }).filter(Boolean)}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                  </>
                )}

                {activeTab === 'profile' && (
                  <div className="profile-at-creation-wrap">
                    {profileLoading && (
                      <div className="profile-at-creation-loading">Loading nutrition profile…</div>
                    )}
                    {!profileLoading && !linkedProfile && (
                      <div className="profile-at-creation-empty">
                        No nutrition profile was linked to this meal plan.
                      </div>
                    )}
                    {!profileLoading && linkedProfile && (() => {
                      const h = linkedProfile.heightCm != null ? Number(linkedProfile.heightCm) : 0;
                      const w = linkedProfile.bodyWeightKg != null ? Number(linkedProfile.bodyWeightKg) : 0;
                      const bmi = h > 0 && w > 0 ? (w / ((h / 100) ** 2)).toFixed(1) : null;
                      const pregnantLactating =
                        linkedProfile.isPregnant === true || linkedProfile.isLactating === true
                          ? 'Yes'
                          : linkedProfile.isPregnant === false && linkedProfile.isLactating === false
                            ? 'No'
                            : null;
                      const diseasesStr = linkedProfile.medicalHistoryJson?.diseases ?? '';
                      const diseaseTags = diseasesStr ? diseasesStr.split(/\s*,\s*/).filter(Boolean) : [];
                      const personalAndFamily = [
                        linkedProfile.medicalHistoryJson?.personalHistory,
                        linkedProfile.medicalHistoryJson?.familyHistory,
                      ].filter(Boolean).join('; ') || null;
                      return (
                        <div className="profile-collapse-cards">
                          {/* Basics */}
                          <div className="profile-collapse-card">
                            <button
                              type="button"
                              className="profile-collapse-card-header"
                              onClick={() => toggleProfileSection('basics')}
                              aria-expanded={profileSectionsOpen.basics}
                            >
                              <span className="profile-collapse-card-icon-wrap">
                                <User size={18} />
                              </span>
                              <span className="profile-collapse-card-heading">
                                <span className="profile-collapse-card-title">Basics</span>
                                <span className="profile-collapse-card-subtitle">Personal vitals and demographics</span>
                              </span>
                              <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.basics ? 'profile-collapse-card-chevron-open' : ''}`} />
                            </button>
                            {profileSectionsOpen.basics && (
                              <div className="profile-collapse-card-body">
                            <div className="profile-at-creation-grid profile-at-creation-grid-basics">
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">GENDER</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.gender)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">DATE OF BIRTH</span>
                                <span className="profile-at-creation-value">
                                  {linkedProfile.dateOfBirth
                                    ? new Date(linkedProfile.dateOfBirth).toLocaleDateString()
                                    : '-'}
                                </span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">HEIGHT (CM)</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.heightCm)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">BODY WEIGHT (KG)</span>
                                <span className="profile-at-creation-value">
                                  {renderValue(linkedProfile.bodyWeightKg)}
                                  {bmi != null && (
                                    <span className="profile-at-creation-bmi"> (BMI: {bmi})</span>
                                  )}
                                </span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">WAIST (CM)</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.waistCm)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">HIP (CM)</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.hipCm)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">NECK (CM)</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.neckCm)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">LIFE STAGE</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.lifeStage)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">PREGNANT/LACTATING</span>
                                <span className="profile-at-creation-value">{pregnantLactating ?? '-'}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">MOTHER TONGUE</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.motherTongue)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">ACTIVITY LEVEL</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.activityLevel)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">MOTIVATION READINESS</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.motivationReadiness)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">PREVIOUS ATTEMPTS</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.previousDietAttempts)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">SUPPORT AVAILABLE</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.supportSystemAvailable)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">TECH COMFORT</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.technologyComfortLevel)}</span>
                              </div>
                            </div>
                              </div>
                            )}
                          </div>

                          {/* Food Preferences */}
                          <div className="profile-collapse-card">
                            <button
                              type="button"
                              className="profile-collapse-card-header"
                              onClick={() => toggleProfileSection('foodPreferences')}
                              aria-expanded={profileSectionsOpen.foodPreferences}
                            >
                              <span className="profile-collapse-card-icon-wrap">
                                <Utensils size={18} />
                              </span>
                              <span className="profile-collapse-card-heading">
                                <span className="profile-collapse-card-title">Food Preferences</span>
                                <span className="profile-collapse-card-subtitle">Dietary habits and restrictions</span>
                              </span>
                              <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.foodPreferences ? 'profile-collapse-card-chevron-open' : ''}`} />
                            </button>
                            {profileSectionsOpen.foodPreferences && (
                              <div className="profile-collapse-card-body">
                            <div className="profile-at-creation-grid">
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">TYPE OF DIET</span>
                                <span className="profile-at-creation-value profile-at-creation-diet-value">
                                  <span className="profile-at-creation-diet-dot" />
                                  {linkedProfile.preferencesJson?.typesOfDiet
                                    ? String(linkedProfile.preferencesJson.typesOfDiet)
                                    : '-'}
                                </span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">DIET NOTES</span>
                                <span className={`profile-at-creation-value ${!(linkedProfile.preferencesJson?.typesOfDietNotes) ? 'profile-at-creation-placeholder' : ''}`}>
                                  {linkedProfile.preferencesJson?.typesOfDietNotes ?? 'None provided'}
                                </span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">FAVORITE FOODS</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.preferencesJson?.favoriteFood)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">DISLIKED FOODS</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.preferencesJson?.dislikedFood)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">ALLERGIES</span>
                                <span className="profile-at-creation-value">
                                  {linkedProfile.preferencesJson?.allergies != null && linkedProfile.preferencesJson.allergies !== ''
                                    ? linkedProfile.preferencesJson.allergies
                                    : 'None'}
                                </span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">FOOD INTOLERANCES</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.preferencesJson?.foodIntolerances)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">NUTRITIONAL DEFICIENCIES</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.preferencesJson?.nutritionalDeficiencies)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">WATER INTAKE</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.preferencesJson?.waterIntake)}</span>
                              </div>
                              <div className="profile-at-creation-field profile-at-creation-field-full">
                                <span className="profile-at-creation-label">OTHER PREFERENCES</span>
                                <div className="profile-at-creation-other-pref">
                                  {renderValue(linkedProfile.preferencesJson?.otherInformation)}
                                </div>
                              </div>
                            </div>
                              </div>
                            )}
                          </div>

                          {/* Medical History */}
                          <div className="profile-collapse-card">
                            <button
                              type="button"
                              className="profile-collapse-card-header"
                              onClick={() => toggleProfileSection('medicalHistory')}
                              aria-expanded={profileSectionsOpen.medicalHistory}
                            >
                              <span className="profile-collapse-card-icon-wrap">
                                <Stethoscope size={18} />
                              </span>
                              <span className="profile-collapse-card-heading">
                                <span className="profile-collapse-card-title">Medical History</span>
                                <span className="profile-collapse-card-subtitle">Clinical profile and health history</span>
                              </span>
                              <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.medicalHistory ? 'profile-collapse-card-chevron-open' : ''}`} />
                            </button>
                            {profileSectionsOpen.medicalHistory && (
                              <div className="profile-collapse-card-body">
                            <div className="profile-at-creation-grid profile-at-creation-grid-medical">
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">DISEASES</span>
                                <span className="profile-at-creation-value">
                                  {diseaseTags.length > 0 ? (
                                    diseaseTags.map((d, i) => (
                                      <span key={i} className="profile-at-creation-pill">{d.trim()}</span>
                                    ))
                                  ) : (
                                    '-'
                                  )}
                                </span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">DISEASE NOTES</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.medicalHistoryJson?.diseasesNotes)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">MEDICATION</span>
                                <span className={`profile-at-creation-value ${!(linkedProfile.medicalHistoryJson?.medication) ? 'profile-at-creation-placeholder' : ''}`}>
                                  {linkedProfile.medicalHistoryJson?.medication != null && linkedProfile.medicalHistoryJson.medication !== ''
                                    ? linkedProfile.medicalHistoryJson.medication
                                    : 'None'}
                                </span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">MEDICATION NOTES</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.medicalHistoryJson?.medicationNotes)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">PERSONAL & FAMILY HISTORY</span>
                                <span className="profile-at-creation-value">{personalAndFamily ?? '-'}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">OTHER MEDICAL INFO</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.medicalHistoryJson?.otherInformation)}</span>
                              </div>
                            </div>
                              </div>
                            )}
                          </div>

                          {/* Lifestyle */}
                          <div className="profile-collapse-card">
                            <button
                              type="button"
                              className="profile-collapse-card-header"
                              onClick={() => toggleProfileSection('humanContext')}
                              aria-expanded={profileSectionsOpen.humanContext}
                            >
                              <span className="profile-collapse-card-icon-wrap">
                                <UserCog size={18} />
                              </span>
                              <span className="profile-collapse-card-heading">
                                <span className="profile-collapse-card-title">Lifestyle</span>
                                <span className="profile-collapse-card-subtitle">Lifestyle and environmental factors</span>
                              </span>
                              <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.humanContext ? 'profile-collapse-card-chevron-open' : ''}`} />
                            </button>
                            {profileSectionsOpen.humanContext && (
                              <div className="profile-collapse-card-body">
                            <div className="profile-at-creation-grid profile-at-creation-grid-human">
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">ACTIVITY LEVEL</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.humanContextJson?.activityLevel)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">CULTURAL IDENTITY</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.humanContextJson?.cultureIdentity)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">RELIGIOUS RESTRICTIONS</span>
                                <span className="profile-at-creation-value">
                                  {linkedProfile.humanContextJson?.religiousRestrictions?.length
                                    ? linkedProfile.humanContextJson.religiousRestrictions.join(', ')
                                    : '-'}
                                </span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">FOOD RELATIONSHIP</span>
                                <span className="profile-at-creation-value">-</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">SLEEP PATTERNS</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.humanContextJson?.sleepPatterns)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">STRESS LEVELS</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.humanContextJson?.stressLevels)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">WORK SCHEDULE</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.humanContextJson?.workSchedule)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">FAMILY STRUCTURE</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.humanContextJson?.familyStructure)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">COOKING RESP.</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.humanContextJson?.cookingResponsibility)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">SLEEP QUALITY</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.humanContextJson?.sleepQuality)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">SMOKING STATUS</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.humanContextJson?.smokingStatus)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">ALCOHOL CONSUMPTION</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.humanContextJson?.alcoholConsumption)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">MARITAL STATUS</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.humanContextJson?.maritalStatus)}</span>
                              </div>
                            </div>
                              </div>
                            )}
                          </div>

                          {/* Constraints */}
                          <div className="profile-collapse-card">
                            <button
                              type="button"
                              className="profile-collapse-card-header"
                              onClick={() => toggleProfileSection('constraints')}
                              aria-expanded={profileSectionsOpen.constraints}
                            >
                              <span className="profile-collapse-card-icon-wrap">
                                <Settings size={18} />
                              </span>
                              <span className="profile-collapse-card-heading">
                                <span className="profile-collapse-card-title">Constraints</span>
                                <span className="profile-collapse-card-subtitle">Cooking, budget and schedule</span>
                              </span>
                              <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.constraints ? 'profile-collapse-card-chevron-open' : ''}`} />
                            </button>
                            {profileSectionsOpen.constraints && (
                              <div className="profile-collapse-card-body">
                            <div className="profile-at-creation-grid">
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">COOKING FACILITIES</span>
                                <span className="profile-at-creation-value">
                                  {linkedProfile.constraintsJson?.cooking
                                    ? [
                                        linkedProfile.constraintsJson.cooking.has_stove ? 'Stove' : null,
                                        linkedProfile.constraintsJson.cooking.has_microwave ? 'Microwave' : null,
                                        linkedProfile.constraintsJson.cooking.has_refrigerator ? 'Refrigerator' : null,
                                        linkedProfile.constraintsJson.cooking.has_oven ? 'Oven' : null,
                                      ]
                                        .filter(Boolean)
                                        .join(', ') || '-'
                                    : '-'}
                                </span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">COOKING SKILL LEVEL</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.constraintsJson?.cooking?.cooking_skills_level)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">MONTHLY BUDGET</span>
                                <span className="profile-at-creation-value">
                                  {linkedProfile.constraintsJson?.budget?.monthly_budget != null
                                    ? `${linkedProfile.constraintsJson.budget.monthly_budget} ${linkedProfile.constraintsJson.budget?.currency ?? ''}`.trim()
                                    : '-'}
                                </span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">WORK HOURS</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.constraintsJson?.schedule?.work_hours)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">MEAL PREP TIME (MIN)</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.constraintsJson?.schedule?.meal_prep_time_min)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">EATING OUT FREQUENCY</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.constraintsJson?.schedule?.eatingOutFrequency)}</span>
                              </div>
                              <div className="profile-at-creation-field profile-at-creation-field-full">
                                <span className="profile-at-creation-label">WON'T DO</span>
                                <span className="profile-at-creation-value">
                                  {linkedProfile.constraintsJson?.wontDo?.length
                                    ? linkedProfile.constraintsJson.wontDo.join(', ')
                                    : '-'}
                                </span>
                              </div>
                            </div>
                              </div>
                            )}
                          </div>

                          {/* Meal Pattern */}
                          <div className="profile-collapse-card">
                            <button
                              type="button"
                              className="profile-collapse-card-header"
                              onClick={() => toggleProfileSection('mealPattern')}
                              aria-expanded={profileSectionsOpen.mealPattern}
                            >
                              <span className="profile-collapse-card-icon-wrap">
                                <CalendarClock size={18} />
                              </span>
                              <span className="profile-collapse-card-heading">
                                <span className="profile-collapse-card-title">Meal Pattern</span>
                                <span className="profile-collapse-card-subtitle">Typical meals and timing</span>
                              </span>
                              <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.mealPattern ? 'profile-collapse-card-chevron-open' : ''}`} />
                            </button>
                            {profileSectionsOpen.mealPattern && (
                              <div className="profile-collapse-card-body">
                            <div className="profile-at-creation-grid">
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">TYPICAL MEALS PER DAY</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.mealPatternJson?.typicalMealsPerDay)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">SNACK FREQUENCY</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.mealPatternJson?.snackFrequency)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">LATE NIGHT EATING</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.mealPatternJson?.lateNightEating)}</span>
                              </div>
                              <div className="profile-at-creation-field">
                                <span className="profile-at-creation-label">WEEKEND PATTERNS</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.mealPatternJson?.weekendPatterns)}</span>
                              </div>
                              <div className="profile-at-creation-field profile-at-creation-field-full">
                                <span className="profile-at-creation-label">MEAL TIMES</span>
                                <span className="profile-at-creation-value">
                                  {linkedProfile.mealPatternJson?.mealTimes && Object.keys(linkedProfile.mealPatternJson.mealTimes).length > 0
                                    ? Object.entries(linkedProfile.mealPatternJson.mealTimes)
                                        .map(([meal, time]) => `${meal}: ${time}`)
                                        .join(', ')
                                    : '-'}
                                </span>
                              </div>
                            </div>
                              </div>
                            )}
                          </div>

                          {/* Notes */}
                          <div className="profile-collapse-card">
                            <button
                              type="button"
                              className="profile-collapse-card-header"
                              onClick={() => toggleProfileSection('notes')}
                              aria-expanded={profileSectionsOpen.notes}
                            >
                              <span className="profile-collapse-card-icon-wrap">
                                <FileText size={18} />
                              </span>
                              <span className="profile-collapse-card-heading">
                                <span className="profile-collapse-card-title">Notes</span>
                                <span className="profile-collapse-card-subtitle">Doctor and patient notes</span>
                              </span>
                              <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.notes ? 'profile-collapse-card-chevron-open' : ''}`} />
                            </button>
                            {profileSectionsOpen.notes && (
                              <div className="profile-collapse-card-body">
                            <div className="profile-at-creation-grid">
                              <div className="profile-at-creation-field profile-at-creation-field-full">
                                <span className="profile-at-creation-label">DOCTOR NOTES</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.doctorNotes)}</span>
                              </div>
                              <div className="profile-at-creation-field profile-at-creation-field-full">
                                <span className="profile-at-creation-label">PATIENT NOTES</span>
                                <span className="profile-at-creation-value">{renderValue(linkedProfile.patientNotes)}</span>
                              </div>
                            </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Top-level Plan Targets tab */}
                {activeTab === 'targets' && (
                  <div className="plan-targets-tab-wrap">
                    {!targetsContext && (
                      <div className="plan-targets-empty">
                        No plan targets were recorded for this meal plan.
                      </div>
                    )}
                    {targetsContext && (
                      <>
                        {/* Metabolic progress card (Body Weight + HbA1c) - no title */}
                        {(() => {
                          const g = targetsContext.goal_specific_info || {};
                          const getVal = (key: string): number | null => {
                            const v = g[key];
                            if (v != null && v !== '') {
                              const n = Number(v);
                              if (!Number.isNaN(n)) return n;
                            }
                            const t = (targetsContext.targets || []).find((x: any) => x?.field_key === key);
                            const tVal = t?.value;
                            if (tVal != null && tVal !== '') {
                              const n = Number(tVal);
                              if (!Number.isNaN(n)) return n;
                            }
                            return null;
                          };
                          const currentWeight = getVal('current_weight_kg');
                          const targetWeight = getVal('target_weight_kg');
                          const currentHba1c = getVal('current_hba1c') ?? getVal('current_hba1c_percent');
                          const targetHba1c = getVal('target_hba1c') ?? getVal('target_hba1c_percent');
                          const weightMin = 60;
                          const weightMax = 80;
                          const hba1cMin = 4;
                          const hba1cMax = 7;
                          const weightCur = currentWeight ?? targetWeight ?? 70;
                          const weightTgt = targetWeight ?? currentWeight ?? 70;
                          const hba1cCur = currentHba1c ?? targetHba1c ?? 5.5;
                          const hba1cTgt = targetHba1c ?? currentHba1c ?? 5.5;
                          const weightPos = (weightCur - weightMin) / (weightMax - weightMin);
                          const weightGoalPos = (weightTgt - weightMin) / (weightMax - weightMin);
                          const hba1cPos = (hba1cCur - hba1cMin) / (hba1cMax - hba1cMin);
                          const hba1cGoalPos = (hba1cTgt - hba1cMin) / (hba1cMax - hba1cMin);
                          const weightDiff = currentWeight != null && targetWeight != null ? currentWeight - targetWeight : null;
                          const hba1cInRange = currentHba1c != null && targetHba1c != null
                            ? Math.abs((currentHba1c - targetHba1c)) <= 0.5
                            : false;
                          return (
                            <div className="metabolic-progress-card">
                              <div className="metabolic-progress-grid">
                                <div className="metabolic-progress-block metabolic-progress-weight">
                                  <div className="metabolic-progress-metric-label">Body Weight</div>
                                  <div className="metabolic-progress-head-row">
                                    <div className="metabolic-progress-current-row">
                                      <span className="metabolic-progress-current-value">{(currentWeight ?? weightCur).toFixed(1)}</span>
                                      <span className="metabolic-progress-current-unit">kg</span>
                                    </div>
                                    <div className="metabolic-progress-target-block">
                                      <span className="metabolic-progress-target-label">TARGET</span>
                                      <span className="metabolic-progress-target-value metabolic-progress-target-value-weight">{(targetWeight ?? weightTgt).toFixed(1)} kg</span>
                                    </div>
                                  </div>
                                  <div className="metabolic-progress-scale-wrap">
                                    <div className="metabolic-progress-scale-labels-above">
                                      <span className="metabolic-progress-scale-goal-label" style={{ left: `${Math.min(100, Math.max(0, weightGoalPos * 100))}%` }}>GOAL</span>
                                    </div>
                                    <div className="metabolic-progress-scale-bar metabolic-progress-scale-bar-weight">
                                      <div
                                        className="metabolic-progress-scale-goal metabolic-progress-scale-goal-weight"
                                        style={{ left: `${Math.min(100, Math.max(0, weightGoalPos * 100))}%` }}
                                      />
                                      <div
                                        className="metabolic-progress-scale-current metabolic-progress-scale-current-weight"
                                        style={{ left: `${Math.min(100, Math.max(0, weightPos * 100))}%` }}
                                      />
                                    </div>
                                    <div className="metabolic-progress-scale-labels-below">
                                      <span className="metabolic-progress-scale-current-label" style={{ left: `${Math.min(100, Math.max(0, weightPos * 100))}%` }}>Current</span>
                                    </div>
                                    <div className="metabolic-progress-scale-range">Scale: {weightMin} – {weightMax}kg</div>
                                  </div>
                                  {weightDiff != null && weightDiff !== 0 && (
                                    <div className="metabolic-progress-status metabolic-progress-status-orange">
                                      <TrendingDown size={16} />
                                      <span>{weightDiff > 0 ? `-${weightDiff.toFixed(1)} kg to target` : `+${Math.abs(weightDiff).toFixed(1)} kg to target`}</span>
                                    </div>
                                  )}
                                  {weightDiff != null && weightDiff === 0 && (
                                    <div className="metabolic-progress-status metabolic-progress-status-green">
                                      <Check size={16} />
                                      <span>At target</span>
                                    </div>
                                  )}
                                </div>
                                <div className="metabolic-progress-block metabolic-progress-hba1c">
                                  <div className="metabolic-progress-metric-label">HbA1c Level</div>
                                  <div className="metabolic-progress-head-row">
                                    <div className="metabolic-progress-current-row">
                                      <span className="metabolic-progress-current-value">{(currentHba1c ?? hba1cCur).toFixed(1)}</span>
                                      <span className="metabolic-progress-current-unit">%</span>
                                    </div>
                                    <div className="metabolic-progress-target-block">
                                      <span className="metabolic-progress-target-label">TARGET</span>
                                      <span className="metabolic-progress-target-value metabolic-progress-target-value-hba1c">{(targetHba1c ?? hba1cTgt).toFixed(1)}%</span>
                                    </div>
                                  </div>
                                  <div className="metabolic-progress-scale-wrap">
                                    <div className="metabolic-progress-scale-labels-above">
                                      <span className="metabolic-progress-scale-goal-label metabolic-progress-scale-goal-label-hba1c" style={{ left: `${Math.min(100, Math.max(0, hba1cGoalPos * 100))}%` }}>GOAL</span>
                                    </div>
                                    <div className="metabolic-progress-scale-bar metabolic-progress-scale-bar-hba1c">
                                      <div
                                        className="metabolic-progress-scale-goal metabolic-progress-scale-goal-hba1c"
                                        style={{ left: `${Math.min(100, Math.max(0, hba1cGoalPos * 100))}%` }}
                                      />
                                      <div
                                        className="metabolic-progress-scale-current metabolic-progress-scale-current-hba1c"
                                        style={{ left: `${Math.min(100, Math.max(0, hba1cPos * 100))}%` }}
                                      />
                                    </div>
                                    <div className="metabolic-progress-scale-labels-below">
                                      <span className="metabolic-progress-scale-current-label" style={{ left: `${Math.min(100, Math.max(0, hba1cPos * 100))}%` }}>Current</span>
                                    </div>
                                    <div className="metabolic-progress-scale-range">Scale: {hba1cMin.toFixed(1)} – {hba1cMax.toFixed(1)}%</div>
                                  </div>
                                  {hba1cInRange && (
                                    <div className="metabolic-progress-status metabolic-progress-status-green">
                                      <Check size={16} />
                                      <span>Maintaining Range</span>
                                    </div>
                                  )}
                                  {currentHba1c != null && targetHba1c != null && !hba1cInRange && (
                                    <div className="metabolic-progress-status metabolic-progress-status-orange">
                                      <TrendingDown size={16} />
                                      <span>{(currentHba1c - targetHba1c) > 0 ? `${(currentHba1c - targetHba1c).toFixed(1)}% above target` : `${(targetHba1c - currentHba1c).toFixed(1)}% to target`}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {Array.isArray((targetsContext as any).derived_adjustments) &&
                      ((targetsContext as any).derived_adjustments?.length || 0) > 0 &&
                      (() => {
                        const allRows = (targetsContext as any).derived_adjustments || [];
                        const doctorModifiedRows = allRows.filter(
                          (row: any) => row?.label === 'ICMR → Doctor Modified'
                        );
                        if (doctorModifiedRows.length === 0) return null;
                        const macroRows = doctorModifiedRows.filter(
                          (r: any) => (NUTRIENT_DELTA_MATRIX_CONFIG[r?.nutrient_key]?.section ?? 'vitamins_minerals') === 'macronutrients'
                        );
                        const vitaminMineralRows = doctorModifiedRows.filter(
                          (r: any) => (NUTRIENT_DELTA_MATRIX_CONFIG[r?.nutrient_key]?.section ?? 'vitamins_minerals') === 'vitamins_minerals'
                        );
                        const vitaminRows = vitaminMineralRows.filter((r: any) => (r?.nutrient_key || '').startsWith('vitamin_'));
                        const mineralRows = vitaminMineralRows.filter((r: any) => !(r?.nutrient_key || '').startsWith('vitamin_'));
                        const maxAbsPctMacro = Math.max(
                          1,
                          ...macroRows.map((r: any) => Math.abs(r?.change_percent ?? 0))
                        );
                        const toStandardUnit = (value: number, unit: string, section: 'vitamin' | 'mineral'): number => {
                          const u = (unit || '').toLowerCase().replace(/\s*\([^)]*\)\s*/g, '').trim();
                          if (section === 'vitamin') {
                            if (u === 'mg' || u === 'g' || u.startsWith('gram')) return value * 1000;
                            return value;
                          }
                          if (section === 'mineral') {
                            if (u === 'mcg' || u === 'µg') return value / 1000;
                            return value;
                          }
                          return value;
                        };
                        const getNormalizedDelta = (row: any, section: 'vitamin' | 'mineral') => {
                          const cfg = NUTRIENT_DELTA_MATRIX_CONFIG[row?.nutrient_key];
                          const unit = cfg?.unit ?? '';
                          const oldV = Number(row?.old_value) || 0;
                          const newV = Number(row?.new_value) || 0;
                          const oldStd = toStandardUnit(oldV, unit, section);
                          const newStd = toStandardUnit(newV, unit, section);
                          return Math.abs(newStd - oldStd);
                        };
                        const maxNormDeltaVit = Math.max(0, ...vitaminRows.map((r: any) => getNormalizedDelta(r, 'vitamin')));
                        const maxNormDeltaMin = Math.max(0, ...mineralRows.map((r: any) => getNormalizedDelta(r, 'mineral')));
                        return (
                          <div className="nutrient-delta-matrix">
                            <div className="nutrient-delta-matrix-header">
                              <h3 className="nutrient-delta-matrix-title">NUTRIENT DELTA MATRIX</h3>
                              <div className="nutrient-delta-matrix-legend">
                                <span className="nutrient-delta-matrix-legend-item">
                                  <span className="nutrient-delta-matrix-legend-dot nutrient-delta-matrix-legend-dot-reduction" />
                                  REDUCTION
                                </span>
                                <span className="nutrient-delta-matrix-legend-item">
                                  <span className="nutrient-delta-matrix-legend-dot nutrient-delta-matrix-legend-dot-increase" />
                                  INCREASE
                                </span>
                              </div>
                            </div>
                            <div className="nutrient-delta-matrix-baseline-row">
                              <div className="nutrient-delta-matrix-baseline-spacer" aria-hidden />
                              <div className="nutrient-delta-matrix-baseline-wrap">
                                <div className="nutrient-delta-matrix-baseline-pill">ICMR BASELINE</div>
                                <div className="nutrient-delta-matrix-baseline-line" />
                              </div>
                              <div className="nutrient-delta-matrix-baseline-spacer" aria-hidden />
                            </div>
                            <div className="nutrient-delta-matrix-body">
                              {macroRows.length > 0 && (
                                <div className="nutrient-delta-matrix-section">
                                  <div className="nutrient-delta-matrix-section-title">MACRONUTRIENTS</div>
                                  {macroRows.map((row: any, idx: number) => {
                                    const cfg = NUTRIENT_DELTA_MATRIX_CONFIG[row?.nutrient_key];
                                    const label = cfg?.label ?? formatKeyLabel(row?.nutrient_key || '');
                                    const unit = cfg?.unit ?? '';
                                    const oldV = Number(row?.old_value) || 0;
                                    const newV = Number(row?.new_value) || 0;
                                    const absDelta = row?.change_absolute != null ? Number(row.change_absolute) : newV - oldV;
                                    const pct = row?.change_percent != null ? Number(row.change_percent) : (oldV ? ((newV - oldV) / oldV) * 100 : 0);
                                    const isIncrease = absDelta >= 0;
                                    const rawBarPct = maxAbsPctMacro > 0 ? (Math.abs(pct) / maxAbsPctMacro) * 100 : 0;
                                    const barPct = absDelta === 0 ? 0 : Math.min(100, rawBarPct);
                                    return (
                                      <div key={`macro-${row?.nutrient_key}-${idx}`} className="nutrient-delta-matrix-row">
                                        <div className="nutrient-delta-matrix-row-label">
                                          <span className="nutrient-delta-matrix-row-name">{label}</span>
                                          <span className="nutrient-delta-matrix-row-unit">{unit}</span>
                                        </div>
                                        <div className="nutrient-delta-matrix-row-chart">
                                          <div className="nutrient-delta-matrix-row-bar-wrap">
                                            <div className="nutrient-delta-matrix-row-baseline-value">{oldV.toFixed(1)}</div>
                                            <div className="nutrient-delta-matrix-row-bar-container">
                                              <div className="nutrient-delta-matrix-row-bar-half nutrient-delta-matrix-row-bar-half-left">
                                                {!isIncrease && (
                                                  <div
                                                    className="nutrient-delta-matrix-row-bar nutrient-delta-matrix-row-bar-reduction"
                                                    style={{ width: `${barPct}%` }}
                                                  >
                                                    <span className="nutrient-delta-matrix-row-bar-value">{newV.toFixed(1)}</span>
                                                  </div>
                                                )}
                                              </div>
                                              <div className="nutrient-delta-matrix-row-baseline-marker" />
                                              <div className="nutrient-delta-matrix-row-bar-half nutrient-delta-matrix-row-bar-half-right">
                                                {isIncrease && (
                                                  <div
                                                    className="nutrient-delta-matrix-row-bar nutrient-delta-matrix-row-bar-increase"
                                                    style={{ width: `${barPct}%` }}
                                                  >
                                                    <span className="nutrient-delta-matrix-row-bar-value">{newV.toFixed(1)}</span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className={`nutrient-delta-matrix-row-delta ${isIncrease ? 'nutrient-delta-matrix-row-delta-increase' : 'nutrient-delta-matrix-row-delta-reduction'}`}>
                                          <span>{absDelta > 0 ? '+' : ''}{absDelta.toFixed(1)}</span>
                                          <span className="nutrient-delta-matrix-row-delta-pct">({absDelta > 0 ? '+' : ''}{pct.toFixed(1)}%)</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {vitaminRows.length > 0 && (
                                <div className="nutrient-delta-matrix-section">
                                  <div className="nutrient-delta-matrix-section-title">VITAMINS</div>
                                  {vitaminRows.map((row: any, idx: number) => {
                                    const cfg = NUTRIENT_DELTA_MATRIX_CONFIG[row?.nutrient_key];
                                    const label = cfg?.label ?? formatKeyLabel(row?.nutrient_key || '');
                                    const unit = cfg?.unit ?? '';
                                    const oldV = Number(row?.old_value) || 0;
                                    const newV = Number(row?.new_value) || 0;
                                    const absDelta = row?.change_absolute != null ? Number(row.change_absolute) : newV - oldV;
                                    const pct = row?.change_percent != null ? Number(row.change_percent) : (oldV ? ((newV - oldV) / oldV) * 100 : 0);
                                    const isIncrease = absDelta >= 0;
                                    const normalizedDelta = getNormalizedDelta(row, 'vitamin');
                                    const barPct = maxNormDeltaVit > 0 ? Math.min(100, (normalizedDelta / maxNormDeltaVit) * 100) : 0;
                                    return (
                                      <div key={`vit-${row?.nutrient_key}-${idx}`} className="nutrient-delta-matrix-row">
                                        <div className="nutrient-delta-matrix-row-label">
                                          <span className="nutrient-delta-matrix-row-name">{label}</span>
                                          <span className="nutrient-delta-matrix-row-unit">{unit}</span>
                                        </div>
                                        <div className="nutrient-delta-matrix-row-chart">
                                          <div className="nutrient-delta-matrix-row-bar-wrap">
                                            <div className="nutrient-delta-matrix-row-baseline-value">{oldV.toFixed(1)}</div>
                                            <div className="nutrient-delta-matrix-row-bar-container">
                                              <div className="nutrient-delta-matrix-row-bar-half nutrient-delta-matrix-row-bar-half-left">
                                                {!isIncrease && (
                                                  <div
                                                    className="nutrient-delta-matrix-row-bar nutrient-delta-matrix-row-bar-reduction"
                                                    style={{ width: `${barPct}%` }}
                                                  >
                                                    <span className="nutrient-delta-matrix-row-bar-value">{newV.toFixed(1)}</span>
                                                  </div>
                                                )}
                                              </div>
                                              <div className="nutrient-delta-matrix-row-baseline-marker" />
                                              <div className="nutrient-delta-matrix-row-bar-half nutrient-delta-matrix-row-bar-half-right">
                                                {isIncrease && (
                                                  <div
                                                    className="nutrient-delta-matrix-row-bar nutrient-delta-matrix-row-bar-increase"
                                                    style={{ width: `${barPct}%` }}
                                                  >
                                                    <span className="nutrient-delta-matrix-row-bar-value">{newV.toFixed(1)}</span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className={`nutrient-delta-matrix-row-delta ${isIncrease ? 'nutrient-delta-matrix-row-delta-increase' : 'nutrient-delta-matrix-row-delta-reduction'}`}>
                                          <span>{absDelta > 0 ? '+' : ''}{absDelta.toFixed(1)}</span>
                                          <span className="nutrient-delta-matrix-row-delta-pct">({absDelta > 0 ? '+' : ''}{pct.toFixed(1)}%)</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {mineralRows.length > 0 && (
                                <div className="nutrient-delta-matrix-section">
                                  <div className="nutrient-delta-matrix-section-title">MINERALS</div>
                                  {mineralRows.map((row: any, idx: number) => {
                                    const cfg = NUTRIENT_DELTA_MATRIX_CONFIG[row?.nutrient_key];
                                    const label = cfg?.label ?? formatKeyLabel(row?.nutrient_key || '');
                                    const unit = cfg?.unit ?? '';
                                    const oldV = Number(row?.old_value) || 0;
                                    const newV = Number(row?.new_value) || 0;
                                    const absDelta = row?.change_absolute != null ? Number(row.change_absolute) : newV - oldV;
                                    const pct = row?.change_percent != null ? Number(row.change_percent) : (oldV ? ((newV - oldV) / oldV) * 100 : 0);
                                    const isIncrease = absDelta >= 0;
                                    const normalizedDelta = getNormalizedDelta(row, 'mineral');
                                    const barPct = maxNormDeltaMin > 0 ? Math.min(100, (normalizedDelta / maxNormDeltaMin) * 100) : 0;
                                    return (
                                      <div key={`min-${row?.nutrient_key}-${idx}`} className="nutrient-delta-matrix-row">
                                        <div className="nutrient-delta-matrix-row-label">
                                          <span className="nutrient-delta-matrix-row-name">{label}</span>
                                          <span className="nutrient-delta-matrix-row-unit">{unit}</span>
                                        </div>
                                        <div className="nutrient-delta-matrix-row-chart">
                                          <div className="nutrient-delta-matrix-row-bar-wrap">
                                            <div className="nutrient-delta-matrix-row-baseline-value">{oldV.toFixed(1)}</div>
                                            <div className="nutrient-delta-matrix-row-bar-container">
                                              <div className="nutrient-delta-matrix-row-bar-half nutrient-delta-matrix-row-bar-half-left">
                                                {!isIncrease && (
                                                  <div
                                                    className="nutrient-delta-matrix-row-bar nutrient-delta-matrix-row-bar-reduction"
                                                    style={{ width: `${barPct}%` }}
                                                  >
                                                    <span className="nutrient-delta-matrix-row-bar-value">{newV.toFixed(1)}</span>
                                                  </div>
                                                )}
                                              </div>
                                              <div className="nutrient-delta-matrix-row-baseline-marker" />
                                              <div className="nutrient-delta-matrix-row-bar-half nutrient-delta-matrix-row-bar-half-right">
                                                {isIncrease && (
                                                  <div
                                                    className="nutrient-delta-matrix-row-bar nutrient-delta-matrix-row-bar-increase"
                                                    style={{ width: `${barPct}%` }}
                                                  >
                                                    <span className="nutrient-delta-matrix-row-bar-value">{newV.toFixed(1)}</span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className={`nutrient-delta-matrix-row-delta ${isIncrease ? 'nutrient-delta-matrix-row-delta-increase' : 'nutrient-delta-matrix-row-delta-reduction'}`}>
                                          <span>{absDelta > 0 ? '+' : ''}{absDelta.toFixed(1)}</span>
                                          <span className="nutrient-delta-matrix-row-delta-pct">({absDelta > 0 ? '+' : ''}{pct.toFixed(1)}%)</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                      </>
                    )}
                  </div>
                )}

                {/* Meal Plans (list/create/view/edit) are now separate pages: MealPlanListPage, MealPlanCreatePage, MealPlanViewPage, MealPlanEditPage */}

                {/* Top-level Q&A tab */}
                {activeTab === 'qa' && (
                  <>
                    {!targetsContext && (
                      <div style={{ color: '#6b7280', fontSize: '0.95rem' }}>
                        No configuration Q&amp;A was recorded for this meal plan.
                      </div>
                    )}
                    {targetsContext &&
                      Array.isArray(targetsContext.targets) &&
                      (targetsContext.targets?.length || 0) > 0 && (
                        <div style={{ marginTop: '8px', marginBottom: '16px' }}>
                          <div
                            style={{
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb',
                              backgroundColor: '#ffffff',
                            }}
                          >
                            {(targetsContext.targets || []).map((t: any, idx: number) => {
                              const fieldKey = t.field_key as string | undefined;
                              const question = (t.question as string | undefined) || formatKeyLabel(fieldKey || '');
                              const answer = t.value ?? (fieldKey && (targetsContext.goal_specific_info || {})[fieldKey]) ?? null;
                              return (
                                <div
                                  key={`${fieldKey || 'q'}-${idx}`}
                                  style={{
                                    padding: '10px 12px',
                                    borderTop: idx === 0 ? 'none' : '1px solid #e5e7eb',
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: '0.8rem',
                                      color: '#6b7280',
                                      marginBottom: '2px',
                                    }}
                                  >
                                    {fieldKey || 'field'}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.9rem',
                                      fontWeight: 600,
                                      color: '#111827',
                                      marginBottom: '4px',
                                    }}
                                  >
                                    {question || '-'}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.9rem',
                                      color: '#111827',
                                    }}
                                  >
                                    Answer: {renderValue(answer)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                  </>
                )}

                {/* Monitoring tab */}
                {activeTab === 'monitoring' && patientId && (
                    <MealPlanMonitoringTab 
                      patientId={Number(patientId)} 
                      goalId={goalId ? Number(goalId) : undefined}
                      mealPlanTargets={dailyTotals}
                    />
                )}

                {activeTab === 'summary' && (
                  <>
                    <div className="meal-plan-meals-grid">
                      {renderMealSection('Breakfast', 'breakfast')}
                      {renderMealSection('Lunch', 'lunch')}
                      {renderMealSection('Dinner', 'dinner')}
                      {renderMealSection('Snacks', 'snacks')}
                    </div>
                    {(() => {
                      const gl = (mealPlan as any)?.grocery_list ?? (mealPlan?.mealPlan as any)?.grocery_list;
                      const list = Array.isArray(gl) ? gl : [];
                      if (list.length === 0) return null;
                      return (
                        <div className="grocery-list-section">
                          <h3 className="grocery-list-title">Grocery List</h3>
                          <div className="grocery-list-table-wrap">
                            <table className="grocery-list-table">
                              <thead>
                                <tr>
                                  <th>Item</th>
                                  <th>Amount</th>
                                  <th>Category</th>
                                </tr>
                              </thead>
                              <tbody>
                                {list.map((gi: any, gIdx: number) => {
                                  const name = typeof gi === 'string' ? gi : gi.ingredient_name || gi.name || gi.item || '—';
                                  const amount = typeof gi === 'object' ? (gi.total_amount ?? gi.quantity ?? gi.amount ?? '') : '';
                                  const unit = typeof gi === 'object' ? (gi.unit ?? gi.portion_display ?? '') : '';
                                  const displayAmount = [amount, unit].filter(Boolean).join(' ') || '—';
                                  const category = typeof gi === 'object' ? (gi.category ?? gi.food_group ?? '') : '';
                                  return (
                                    <tr key={gIdx}>
                                      <td>{name}</td>
                                      <td>{displayAmount}</td>
                                      <td>{category || '—'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default DietPlanDetailPage;
